import { Injectable } from '@angular/core';
import { HomeworkLink } from '../models/homework-link';
import { CommunicationService } from './communication.service';
import { SearchResult } from '../models/search-result';
import { ApplicationStateService } from './application-state.service';
import { QuickLinkService } from './quick-link.service';
import { QuickLink, QuickLinkType } from '../models/quick-link';
import { environment } from '../../environments/environment';
import { RouteService } from './route.service';
import { RestService } from './rest.service';
import { UtilityService } from './utility.service';
import { CryptService } from './crypt.service';
import { HomeworkFactory } from '../models/factories/homework-factory';
import { ModalService } from './modal.service';
import { CookieService } from 'ngx-cookie-service';
import { QuickLinkUtils } from '../utils/quick-link-utils';
import { Observable, Subject } from 'rxjs';
import { SidebarService } from './sidebar.service';
import { SidebarChangedEvent } from '../utils/events/sidebar-changed-event';
import { TasksComponent } from '../components/sidebar/tasks/tasks.component';
import { HomeworkComponent } from '../components/sidebar/tasks/homework/homework.component';
import { TestGeneratorComponent } from '../components/sidebar/tasks/test-generator/test-generator.component';
import { AlertService } from './alert.service';
import { SubAssignment } from '../models/sub-assignment';
import { CrudService } from './crud.service';
import { TechnicalFeature } from '@lemberger/lemberger-am';

@Injectable({
    providedIn: 'root'
})
export class HomeworkService {

    showHomeworkButton = true;
    tests: Array<HomeworkLink>;
    addHomeworkExerciseSubject: Subject<object> = new Subject();
    addHomeworkExercise$: Observable<object> = this.addHomeworkExerciseSubject.asObservable();
    token = '';
    nickname = '';
    loadingIndicator = false;
    testGeneratorOpened = false;
    formAddedTests: Array<SearchResult> = [];
    formAddedHomeworks: Array<SearchResult> = [];
    homeworks: Array<Array<HomeworkLink>>;
    private opened = false;
    private editMode = false;
    private activeMode = false;
    private actualHomework: HomeworkLink;
    private TOKEN_KEY = 'TOKEN';
    private readonly RETRY_INTERVAL_BASE = 1000;
    private retryInterval = this.RETRY_INTERVAL_BASE;

    constructor(private routeService: RouteService,
                private restService: RestService,
                private sidebarService: SidebarService,
                private applicationStateService: ApplicationStateService,
                private quickLinkService: QuickLinkService,
                private utilityService: UtilityService,
                private alertService: AlertService,
                private cryptService: CryptService,
                private cookieService: CookieService,
                private communicationService: CommunicationService,
                private modalService: ModalService,
                private crudService: CrudService) {

        this.actualHomework = new HomeworkLink();
        if (this.applicationStateService.userData && this.applicationStateService.userData.email) {
            this.actualHomework.email = this.applicationStateService.userData.email;
        }

        this.communicationService.addHomeworkAnnounced$.subscribe(() => {
            this.editMode = true;
            this.activeMode = true;
        });

        this.communicationService.clearHomeworkAnnounced$.subscribe(() => {

            this.actualHomework = new HomeworkLink();
            this.formAddedTests = [];
            this.formAddedHomeworks = [];

            if (this.applicationStateService.userData && this.applicationStateService.userData.email) {
                this.actualHomework.email = this.applicationStateService.userData.email;
            }

            this.communicationService.announceHomeworkChange();
            this.editMode = false;
            this.activeMode = false;
        });
    }

    get portfolioLink(): string {
        if (this.applicationStateService.userData.userIdentifier) {
            return `https://o-le.org/ikorr/Gesamt_Kompetenzauswertung_TGr_${this.applicationStateService.userData.userIdentifier}.xlsx/`;
        } else if (this.applicationStateService.userData.bookAccessCode) {
            return `https://o-le.org/ikorr/Gesamt_Kompetenzauswertung_TGr_${this.applicationStateService.userData.bookAccessCode.quickLink}.xlsx/`;
        }
    }

    /**
     * Get homework links
     *
     * @returns {Promise<Array<HomeworkLink>>}
     * @param tests
     */
    getHomeworkLinks(tests?): Promise<Array<Array<HomeworkLink>>> {
        const route = this.routeService.getRelativeRoute(environment.endpointUrls.hsmLinks, {
            bookId: this.applicationStateService.bookId
        });

        return new Promise<Array<Array<HomeworkLink>>>((resolve, reject) => {

            return this.restService.get(route, {
                book: this.applicationStateService.bookId
            }).then(result => {

                const homeworkFactory = new HomeworkFactory();
                const homeworks = [];
                const homeworkUsed = [];
                const homeworkTeamTeaching = [];
                // max. 20 elements should be shown in the list.
                const maxElementsListed = 20;

                result.homework.forEach((hw, index) => {
                    if (index < maxElementsListed) {
                        const homework = homeworkFactory.buildFromObject(hw, true);
                        homework.bypassCheck = this.applicationStateService.bookConfig.bookPermissions.homework.view;
                        homeworks.push(homework);
                    }
                });

                result.homeworkUsed.forEach((hw, index) => {
                    if (index < maxElementsListed) {
                        const homework = homeworkFactory.buildFromObject(hw, true);
                        homework.bypassCheck = this.applicationStateService.bookConfig.bookPermissions.homework.view;
                        homeworkUsed.push(homework);
                    }
                });

                result.homeworkTeamTeaching.forEach((hw, index) => {
                    if (index < maxElementsListed) {
                        const homework = homeworkFactory.buildFromObject(hw, true);
                        homework.bypassCheck = this.applicationStateService.bookConfig.bookPermissions.homework.view;
                        homeworkTeamTeaching.push(homework);
                    }
                });

                this.homeworks = [homeworks, homeworkUsed, homeworkTeamTeaching];
                resolve(this.filterTests(this.homeworks, tests));
            }).catch(error => {
                reject(error);
            });
        });
    }

    /**
     * Saves the homework to the server.
     */
    async saveHomework(test = false): Promise<HomeworkLink> {

        const now = Math.floor(Date.now() / 1000);

        const dueTimestamp = now + 365 * 24 * 60 * 60;

        const quickLinkMap = this.actualHomework.exercises.map((ele) => {
            if (test) {
                return ele.text;
            }

            let quickLink = ele.text;
            if (ele.subAssignments?.length > 0) {
                ele.subAssignments.forEach((subAssignment) => {
                    // https://redmine.sofamedia.at/issues/20921#note-36 submit subassignments with index + 1
                    quickLink += (subAssignment.checked) ? `.${subAssignment.index + 1}` : '';
                });
            }

            return quickLink;
        });

        this.saveTemplate(quickLinkMap, now);

        const reducer = (accumulator, currentValue) => `${accumulator},${currentValue}`;
        let quickLinkList = quickLinkMap.reduce(reducer);
        quickLinkList = quickLinkList.substr(0, quickLinkList.length);

        const issuer = this.actualHomework.email || '';

        const domain = this.applicationStateService.platformDomain;
        const target = `https://${domain}/hsm`;

        const dynamicQuicklink = {
            issuer,
            issuerHash: this.utilityService.hash(issuer),
            issuerEnc: this.cryptService.encryptSaltedString(issuer),
            created: now,
            expires: dueTimestamp,
            description: this.actualHomework.title,
            charge: 1,
            type: 'home-assignment',
            visible: [now, dueTimestamp],
            'hand-in': [now, dueTimestamp],
            domain: this.applicationStateService.platformDomain,
            quicklink: null,
            qrcode: null,
            target: `${target}?mailto=${this.actualHomework.email}&assignments=${quickLinkList}`,
            printLink: null,
            contents: this.actualHomework.exercises,
            'teacher-token': [this.utilityService.hash(this.actualHomework.email + now)],
            'student-token': []
        };

        if (test) {
            dynamicQuicklink.target += `&cooldown=${this.actualHomework.cooldownPhase}`;
        }

        const quickLinkCreateUrl = this.routeService.getRelativeRoute(environment.endpointUrls.quickLinkRedirectCreate, []);
        const quickLinkResult = await this.restService.post(quickLinkCreateUrl, dynamicQuicklink);
        dynamicQuicklink.quicklink = quickLinkResult.quicklink;
        dynamicQuicklink.printLink = `${target}?assignments=${quickLinkList}&homeworkLink=${dynamicQuicklink.quicklink}&print=homework`;
        // Disabled by #22066
        // const url = this.routeService.getRelativeRoute(environment.endpointUrls.hsmLoggingQueue, []);
        // const targetUrl = `${environment.endpointUrls.assignmentLink}/template/index.php?sofa=1`;
        //
        // const data = {
        //     target: targetUrl,
        //     method: 'POST',
        //     params: {
        //         key: 'jgh98fdt4$poiu',
        //         dynQL: dynamicQuicklink
        //     }
        // }
        //
        // await this.restService.post(url, data);

        const homework = new HomeworkLink();
        homework.title = dynamicQuicklink.description;
        homework.email = dynamicQuicklink.issuer;
        homework.domain = dynamicQuicklink.domain;
        homework.link = dynamicQuicklink.quicklink;
        if (!dynamicQuicklink.quicklink && this.applicationStateService.platformDomain !== 'digi.schule') {
            homework.link = `QL${Math.floor(Math.random() * 9000) + 1000}`;
        }
        if (this.actualHomework.secondEmail) {
            homework.secondEmail = this.actualHomework.secondEmail;
        }
        homework.cooldownPhase = this.actualHomework.cooldownPhase;
        homework.created = now;
        homework.exercises = dynamicQuicklink.contents;
        homework.test = test;
        homework.qrCode = this.routeService.getAbsoluteRoute(
            'rest.utils.qr-code.generate',
            { 'url': `${this.applicationStateService.baseUrl}/${homework.link}`, 'size': 200 }
        );
        homework.printLink = dynamicQuicklink.printLink;

        await this.sendHomeworkEmail(homework, now);

        if (!this.applicationStateService.userData.bookAccessCode) {
            this.crudService.logDirect(TechnicalFeature.HomeworkCreation);
            this.communicationService.announceSavedHomework(homework);

            return homework;
        }

        const storedHomeWorkResult = await this.storeInBackend(homework, now, quickLinkMap);
        homework.id = storedHomeWorkResult.id;
        this.crudService.logDirect(TechnicalFeature.HomeworkCreation);
        this.communicationService.announceSavedHomework(homework);

        return homework;
    }

    /**
     * Deletes a homework from the server.
     */
    removeHomework(homework): Promise<any> {
        if (!this.applicationStateService.userData.bookAccessCode || !homework.id) {
            Promise.resolve();
        }
        const route = this.routeService.getRelativeRoute(
            environment.endpointUrls.hsmDeleteHomework,
            {
                id: homework.id
            });

        return this.restService.delete(route);
    }

    storeInBackend(homework: HomeworkLink, now: number, quickLinks: Array<string>): Promise<any> {
        homework.created = now;
        const route = this.routeService.getRelativeRoute(environment.endpointUrls.hsmSaveHomework, {});

        let bookAccessCodeQuicklink = null;
        if (this.applicationStateService.userData.bookAccessCode) {
            bookAccessCodeQuicklink = this.applicationStateService.userData.bookAccessCode.quickLink;
        }

        return this.restService.post(route,
            {
                ...homework,
                prefix: (this.applicationStateService.bookConfig.quickLinkPrefix || '').toLowerCase(),
                created: now,
                exercises: quickLinks,
                bookAccessCode: bookAccessCodeQuicklink,
                test: homework.test
            });
    }

    editInBackend(homework: HomeworkLink, quickLinks: Array<string>): Promise<any> {
        const route = this.routeService.getRelativeRoute(environment.endpointUrls.hsmEditHomework, { id: homework.id });

        return this.restService.post(route, { exercises: quickLinks });
    }

    /**
     * is user a teacher
     *
     * @returns {boolean}
     */
    isTeacher(): boolean {
        return this.applicationStateService.userData.isTeacher;
    }

    /**
     * announce to open add homework dialog
     */
    addHomeworkPressed(): void {
        if (!this.editMode) {
            this.actualHomework.title = 'Aufgabe';
            this.actualHomework.email = this.applicationStateService.userData.email;
            this.actualHomework.exercises = [];
            this.communicationService.announceAddHomework();
        }
    }

    /**
     * is homework modal opened
     *
     * @returns {boolean}
     */
    isHomeworkOpened(): boolean {
        return this.opened;
    }

    /**
     * set homework modal open status to true (open)
     */
    openHomework(): void {
        if (environment.features.hsmListActivated) {
            this.opened = true;
        }
    }

    /**
     * set homework modal open status to false (closed)
     */
    closeHomework(): void {
        this.opened = false;
    }

    /**
     * store actual homework
     *
     * @param {HomeworkLink} actualAdded
     */
    syncHomework(actualAdded): void {
        this.actualHomework = actualAdded;
    }

    /**
     * get actual homework
     *
     * @returns {HomeworkLink}
     */
    getActualHomework(): HomeworkLink {
        return this.actualHomework;
    }

    /**
     * check if exercise is added to actual homework
     *
     * @param {string} hw
     * @return {boolean}
     */
    containsActualHomework(hw: string): boolean {
        const result = this.actualHomework.exercises.find(value => {
            return value.text.toUpperCase() === hw.toUpperCase();
        });

        return !!result;
    }

    /**
     * add exercise to actual homework
     *
     * @param {string} text
     * @param {number} pageId
     * @param teaser
     * @param preview
     */
    addExercise(text: string, pageId?, teaser?, preview?): void {
        text = this.quickLinkService.convertQuickLink(text);
        if (!this.containsActualHomework(text)) {
            const value = new SearchResult();
            value.text = text;
            value.pageId = pageId;
            value.teaser = teaser;
            value.preview = preview;

            this.quickLinkService.fetchAssignmentData(text).then((assignmentData) => {
                // only add sub-assignments if assignmentData as more than one assignment
                if (assignmentData?.body && assignmentData.body.assignments && assignmentData.body.assignments.length > 1) {
                    for (const assignment of assignmentData.body.assignments.entries()) {
                        const subAssignment = new SubAssignment();
                        subAssignment.index = assignment[0];
                        subAssignment.nr = ('nr' in assignment[1] && typeof assignment[1].nr === 'string') ? assignment[1].nr : '';

                        value.subAssignments.push(subAssignment);
                    }
                }
                this.actualHomework.exercises.push(value);
                this.communicationService.announceHomeworkChange();
            }).catch(() => {
                this.actualHomework.exercises.push(value);
                this.communicationService.announceHomeworkChange();
            });
        }
    }

    /**
     * remove exercise from actual homework
     */
    removeExercise(text: string):
        void {
        this.actualHomework.exercises = this.actualHomework.exercises.filter(value => {
            return value.text.toUpperCase() !== text.toUpperCase();
        });

        this.communicationService.announceHomeworkChange();
    }

    /**
     * is homework edit mode on
     *
     * @returns {boolean}
     */
    isHomeworkEditorOpened(): boolean {
        return this.editMode;
    }

    /**
     * is homework editor active
     *
     * @return {boolean}
     */
    isHomeworkEditorActive(): boolean {
        return this.activeMode;
    }

    /**
     * activate Homework editor
     */
    activateHomework(): void {
        this.activeMode = true;
    }

    /**
     * deactivate Homework editor.
     */
    deactivateHomework(): void {
        this.activeMode = false;
    }

    /**
     * hide/show homework editor
     */
    triggerHomeworkEditor(): void {
        if (!this.editMode && !this.activeMode) {
            this.activeMode = true;
        }
        this.editMode = !this.editMode;
    }

    /**
     * hide/show homework editor
     */
    openHomeworkEditor(): void {
        if (!this.activeMode) {
            this.activeMode = true;
        }
        this.editMode = true;
    }

    /**
     * checks if quicklink is an exercise
     *
     * @param {QuickLink} quickLink
     * @return {boolean}
     */
    isQuickLinkExercise(quickLink: QuickLink): boolean {

        if (!quickLink.link || (quickLink.type && quickLink.type !== QuickLinkType.OTHER)) {
            return false;
        }

        const combinedLinkAndTmpRedirect = this.quickLinkService.getCombinedLinkAndTmpRedirect(quickLink.link);

        if (!combinedLinkAndTmpRedirect) {
            return false;
        }

        return !combinedLinkAndTmpRedirect.tmpRedirect;
    }

    /**
     * convert quick link to readable chapter + link combination
     *
     * @param {SearchResult} link
     * @returns {array}
     */
    convertExercise(link: SearchResult): string {
        return this.quickLinkService.convertQuickLink(link.text, true).toLowerCase();
    }

    openQuicklink(quicklink: QuickLink, pageId, $event): boolean {
        if (this.openHsmLink(quicklink, pageId, $event)) {

            this.addHomeworkPressed();

            if (this.sidebarService.activeComponent !== TasksComponent) {

                this.sidebarService.triggerSidebarChangeEvent(
                    new SidebarChangedEvent(true, TasksComponent)
                );
            }
            this.testGeneratorOpened = false;
            this.openHomeworkEditor();

            return true;
        }

        return this.quickLinkService.openQuicklink(quicklink);
    }

    /**
     * handle click on hsm page link
     *
     * @param {QuickLink} quickLink
     * @param {number} pageId
     * @param $event
     * @return {boolean}
     */
    openHsmLink(quickLink: QuickLink, pageId: number, $event): boolean {
        const prefix = (this.applicationStateService.bookConfig.quickLinkPrefix || '').toLowerCase();
        const quickLinkLink = quickLink.link || '';
        const prefixIndex = quickLinkLink.toLowerCase().indexOf(prefix);
        const prefixEndIndex = prefixIndex > 0 ? prefixIndex + prefix.length : 0;
        const quickLinkWithoutPrefix = quickLinkLink.substring(prefixEndIndex).toLowerCase();

        if (quickLinkWithoutPrefix.indexOf('hsm') < 0 &&
            quickLinkWithoutPrefix.indexOf('am') < 0) {
            return false;
        }
        if (environment.features.hsmListActivated && this.applicationStateService.bookConfig.hsmActive) {
            $event.preventDefault();

            if (!quickLink.bypassCheck) {
                //this.modalService.openDisabledFeatureModal();

                return false;
            }

            if (quickLink && QuickLinkUtils.getLinkCode(quickLink.link) && !quickLink.dropdownLink) {
                this.loadingIndicator = true;
                this.quickLinkService.getCollectionQuickLinks(quickLink).then(result => {
                    if (result && result.length > 0) {
                        result.forEach(link => {
                            if (this.isQuickLinkExercise(link)) {
                                this.addExercise(link.link, pageId);
                            }
                        });
                    } else {
                        this.addAllActiveQuickLinksToPage(pageId);
                    }
                    this.loadingIndicator = false;

                }).catch(err => {
                    this.addAllActiveQuickLinksToPage(pageId);
                    this.loadingIndicator = false;
                });
            } else if (quickLink.dropdownLink) {
                this.addExercise(quickLink.dropdownLink, pageId);

                this.loadingIndicator = false;
            } else {
                this.loadingIndicator = false;
            }

            return true;
        } else if (environment.features.newHsmActivated) {
            this.modalService.openModal(quickLink);
            this.loadingIndicator = false;

            return true;
        }

        return false;
    }

    /**
     * get full link to load (new) hsm iframe
     *
     * @param {string} link
     * @return {string}
     */
    getHsmLink(link: string): string {
        link = this.quickLinkService.convertQuickLink(link, !link.startsWith('https')).toLowerCase();

        return this.routeService.getAbsoluteRoute(environment.endpointUrls.hsmFrame,
            { quicklink: link, book: this.applicationStateService.bookId });
    }

    getEvaluationLink(homework: HomeworkLink): string {
        return this.routeService.getAbsoluteRoute(environment.endpointUrls.hsmDownloadEvaluation, { timestamp: +homework.created });
    }

    /**
     * check token or get new one
     *
     * @param callback
     * @return {string}
     */
    checkToken(callback): string {
        const nickname = this.getNickname();
        this.cryptService.getSession();
        this.token = this.cookieService.get(this.TOKEN_KEY);

        if (!this.token || nickname !== this.nickname) {
            this.getToken().then(() => {
                callback();
            });

            return;
        }

        return this.token;
    }

    /**
     * get token
     *
     * @return {Promise<string>}
     */
    getToken(): Promise<string> {
        const nickname = this.getNickname();
        const encNickname = this.cryptService.encNick(nickname);
        this.token = this.cookieService.get(this.TOKEN_KEY);

        if (!this.token || nickname !== this.nickname) {
            const update = this.token && nickname !== this.nickname;

            // set token
            let data;
            if (update) {
                data = {
                    actorName: encNickname,
                    docuList: (this.applicationStateService.bookConfig.quickLinkPrefix || '').toLowerCase(),
                    key: 'dOkPfdt4$dOsW',
                    update: true,
                    t: this.token
                };
            } else {
                data = {
                    actorName: encNickname,
                    docuList: (this.applicationStateService.bookConfig.quickLinkPrefix || '').toLowerCase(),
                    key: 'dOkPfdt4$dOsW'
                };
            }

            return new Promise(resolve => {
                const url = `${environment.endpointUrls.assignmentLink}/actorRelay.php?sofa=1`;

                this.restService.get(url, data).then(result => {
                    if (this.token) {
                        if (update) {
                            this.nickname = nickname;
                        }

                        return resolve(this.token);
                    }

                    if (result.token) {
                        this.token = result.token;
                        this.cookieService.set(this.TOKEN_KEY, this.token, null, null, null, true);
                        this.nickname = nickname;

                        return resolve(result.token);
                    }
                }).catch(err => {
                    /*this.restService.retry(() => {
                        this.getToken();
                    });*/
                });
            });
        }

        return new Promise(resolve => resolve(this.token));
    }

    fetchRecommendations(quicklinks: Array<string>): any {
        const route = this.routeService.getRelativeRoute(environment.endpointUrls.recommendations, {});

        return this.restService.get(route, { query: quicklinks });
    }

    async checkAlert(): Promise<boolean> {
        if ((this.sidebarService.activeComponent === TasksComponent ||
                this.sidebarService.activeComponent === HomeworkComponent ||
                this.sidebarService.activeComponent === TestGeneratorComponent) &&
            (this.formAddedHomeworks.length || this.formAddedTests.length)) {
            if (this.isHomeworkEditorOpened()) {
                if (!await this.alertService.alert(AlertService.HOMEWORK_CONFIRMATION)) {
                    return false;
                }
            }
            if (this.testGeneratorOpened) {
                if (!await this.alertService.alert(AlertService.TASKS_CONFIRMATION)) {
                    return false;
                }
            }
        }

        return true;
    }

    sendHomeworkEmail(homework: HomeworkLink, now: number): Promise<any> {
        const route = this.routeService.getRelativeRoute(environment.endpointUrls.hsmSendHomeworkEmail, {});

        return this.restService.post(route,
            {
                ...homework,
                prefix: (this.applicationStateService.bookConfig.quickLinkPrefix || '').toLowerCase(),
                created: now
            });
    }

    // Function to filter inner arrays with HomeworkLink objects having test = true
    private filterTests(homeworks: Array<Array<HomeworkLink>>, tests?: boolean): Array<Array<HomeworkLink>> {
        const filteredHomeworks: Array<Array<HomeworkLink>> = [];

        if (tests === undefined) {
            return homeworks;
        }

        for (const homeworkGroup of homeworks) {
            const filteredGroup: Array<HomeworkLink> = homeworkGroup.filter((homework) => {
                // eslint-disable-next-line eqeqeq
                return homework.test == tests;
            });

            filteredHomeworks.push(filteredGroup);
        }

        return filteredHomeworks;
    }

    private addAllActiveQuickLinksToPage(pageId: number): void {
        const quicklinks = this.quickLinkService.getAllActiveQuickLinksOfPage(pageId);

        quicklinks.genericQuickLinks.forEach(link => {
            if (this.isQuickLinkExercise(link)) {
                this.addExercise(link.link, pageId);
            }
        });
    }

    /**
     * Sends some cool stuff for unknown reasons.
     * @param quickLinkArray
     * @param now
     */
    private saveTemplate(quickLinkArray, now): void {

        this.getToken().then(token => {
            this.retryInterval = this.RETRY_INTERVAL_BASE;
            const session = this.cryptService.getSession();

            for (let index = 0; index < quickLinkArray.length; index++) {
                const logData = {
                    t: token,
                    name: '',
                    tag: `${quickLinkArray[index]}.${index}.${now}`,
                    mat: (this.applicationStateService.bookConfig.quickLinkPrefix || '').toLowerCase(),
                    action: 'attempt',
                    key: 'jgh98fdt4$dOsW',
                    // api key
                    ts: session,
                    // session = timestamp
                    fieldId: index,
                    status: '0',
                    hash: this.utilityService.hash('template'),
                    answer: 'template'
                };

                const url = `${environment.endpointUrls.assignmentLink}/loggingRelay.php?sofa=1`;
                setTimeout(() => {
                    this.sendMetaData(url, logData); // TODO: send to queue server
                }, index * 1000);
            }
        }).catch(err => {
            setTimeout(() => {
                this.saveTemplate(quickLinkArray, now);
                this.retryInterval = Math.min(this.retryInterval * 1.5, 1000 * 60);
            }, this.retryInterval);
        });
    }

    private async sendMetaData(url: string, logData: any): Promise<any> {
        const queueUrl = this.routeService.getRelativeRoute(environment.endpointUrls.hsmLoggingQueue, []);

        const data = {
            target: url,
            method: 'GET',
            params: logData
        };

        try {
            await this.restService.post(queueUrl, data);
        } catch (exception) {
            setTimeout(async () => {
                await this.sendMetaData(url, logData);
            }, this.RETRY_INTERVAL_BASE);
        }
    }

    private getNickname(): string {
        if (this.applicationStateService.userData.firstName || this.applicationStateService.userData.lastName) {
            return `${this.applicationStateService.userData.firstName} ${this.applicationStateService.userData.lastName}`;
        }

        return '';
    }
}
